/* eslint-disable @typescript-eslint/naming-convention */
import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Modal } from 'antd';
import moment from 'moment';
import { Table, Button, DetailsContainer } from '@ui-modules';
import { IDropdownOption } from '@unbooking/ui-modules/lib/types';

import { IDestination, ILineItem, IRecipient } from '@common/interfaces';
import { ReactComponent as FileNewIcon } from '@assets/svg/file-new.svg';
import { ReactComponent as DocumentIcon } from '@assets/svg/document.svg';
import { columnsConfig } from '../../detailsConfig';
import { getDateComponent, getInputComponent } from '../../utils';

import './styles.scss';

interface CurrentLineItem extends Partial<ILineItem> {
  isDraft?: boolean;
  order?: number;
}

export interface Point {
  destination: IDestination;
  id: string;
  lineItems: Array<CurrentLineItem>;
  recipient: IRecipient;
}

interface IMappedLineItems {
  items: ILineItem[];
  volumeAggregator: number;
  weightAggregator: number;
}

interface IMappedLineItemsList extends Partial<Omit<ILineItem, 'weight' | 'volume'>> {
  id?: number;
  volume: string | number;
  weight: string | number;
}

interface IPointBlock {
  goodCategoriesOptions: Array<IDropdownOption>;
  isEditMode: boolean;
  loadingUnitsOptions: Array<IDropdownOption>;
  order: number;
  point: Point;
  setDeletingItems: Dispatch<SetStateAction<string[]>>;
  setPoints: Dispatch<SetStateAction<Array<Point>>>;
  stackableOptions: Array<IDropdownOption>;
  wvUnitsOptions: Array<IDropdownOption>;
}

const MAX_LINE_ITEMS_COUNT = 5;

const mapTableItemsToComponent = (items: ILineItem[]): Array<CurrentLineItem> => {
  if (!items.length) return [];

  let result: IMappedLineItemsList[] = [];

  items.reduce(
    (accumulator, currentValue: ILineItem, index: number) => {
      const currentVolume = currentValue.total_volume ? currentValue.total_volume : 0;
      const currentWeight = currentValue.total_weight ? currentValue.total_weight : 0;
      const volume: any = accumulator.volumeAggregator + currentVolume;
      const weight = accumulator.weightAggregator + currentWeight;

      if (index === items.length - 1) {
        result = [
          ...accumulator.items,
          { ...currentValue, id: index + 1 },
          {
            id: 0,
            uuid: '',
            volume: Number(volume.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2 }),
            weight: Number(weight.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2 }),
          },
        ];
      }

      return {
        items: [...accumulator.items, { ...currentValue, id: index + 1 }],
        weightAggregator: weight,
        volumeAggregator: volume,
      };
    },
    { volumeAggregator: 0, weightAggregator: 0, items: [] } as IMappedLineItems,
  );

  return result as ILineItem[];
};

const DestinationIcon = ({ order }: { order: number }) => {
  const code = String.fromCharCode(order + 97);
  return <div className={`destination-icon dest-${code}`}>{code.toUpperCase()}</div>;
};

const PointBlock: FC<IPointBlock> = ({
  goodCategoriesOptions,
  isEditMode,
  loadingUnitsOptions,
  order,
  point,
  setDeletingItems,
  setPoints,
  stackableOptions,
  wvUnitsOptions,
}) => {
  const { lineItems, destination, recipient } = point;

  const [deletingItemApprove, setDeletingItemApprove] = useState<string | null>(null);

  const deleteItem = (uuid: string, isDraft: boolean) => {
    if (isDraft) {
      setPoints((prevPoints) => {
        const newArray = [...prevPoints];
        const idx = newArray.findIndex(({ destination: dest }) => dest.uuid === destination.uuid);
        if (idx >= 0) {
          newArray[idx].lineItems = newArray[idx].lineItems.filter((item) => item.uuid !== uuid);
        }
        return newArray;
      });
    } else {
      setDeletingItemApprove(uuid);
    }
  };

  return (
    <div className="information-container">
      {destination && (
        <div className="information-block">
          <div className="information-header">
            <h3>
              <DestinationIcon order={order} />
              Destination {order}
            </h3>
            {destination.transport_breakdown_file && (
              <Button
                className={`breakdown_button dest-${String.fromCharCode(order + 97)}`}
                icon={<DocumentIcon />}
                onClick={() => window.open(destination.transport_breakdown_file, '_blank')}
                text="View Transport Breakdown"
              />
            )}
          </div>
          <DetailsContainer
            key="destination"
            className={`custom-destination-grid ${!isEditMode && 'content-overflowed'}`}
            items={[
              {
                label: (
                  <>
                    <DestinationIcon order={order} />
                    DESTINATION LOCATION
                  </>
                ),
                content:
                  getInputComponent(
                    destination.dropoff_location_name,
                    isEditMode,
                    `destination:${destination.uuid}:dropoff_location_name`,
                  ) ?? '',
              },
              {
                label: (
                  <>
                    <DestinationIcon order={order} />
                    DESTINATION LOCATION ADDRESS/DETAILS
                  </>
                ),
                content:
                  getInputComponent(
                    destination.dropoff_location,
                    isEditMode,
                    `destination:${destination.uuid}:dropoff_location`,
                  ) ?? '',
              },
              {
                label: (
                  <>
                    <DestinationIcon order={order} />
                    DELIVERY COMPLETION DUE DATE
                  </>
                ),
                content: !isEditMode
                  ? moment(new Date(destination.dropoff_date)).format('DD/MM/YYYY') ?? '-'
                  : getDateComponent(
                      destination.dropoff_date,
                      true,
                      `destination:${destination.uuid}:dropoff_date`,
                    ) ?? '',
              },
            ]}
          />
        </div>
      )}
      {recipient && (
        <div className="information-block">
          <h3>
            <DestinationIcon order={order} />
            Destination {order} - Recipient information
          </h3>
          <DetailsContainer
            key="recipient"
            items={[
              {
                label: (
                  <>
                    <DestinationIcon order={order} />
                    RECIPIENT FULLNAME
                  </>
                ),
                content: recipient.full_name,
              },
              {
                label: (
                  <>
                    <DestinationIcon order={order} />
                    RECIPIENT ORGANISATION
                  </>
                ),
                content:
                  getInputComponent(
                    recipient.organisation,
                    isEditMode,
                    `recipient:${recipient.uuid}:organisation`,
                  ) ?? '',
              },
              {
                label: (
                  <>
                    <DestinationIcon order={order} />
                    RECIPIENT EMAIL
                  </>
                ),
                content:
                  getInputComponent(
                    recipient.email,
                    isEditMode,
                    `recipient:${recipient.uuid}:email`,
                  ) ?? '',
              },
              {
                label: (
                  <>
                    <DestinationIcon order={order} />
                    RECIPIENT PHONE NUMBER
                  </>
                ),
                content:
                  getInputComponent(
                    recipient.phone_number,
                    isEditMode,
                    `recipient:${recipient.uuid}:phone_number`,
                  ) ?? '',
              },
            ]}
          />
        </div>
      )}
      <div className="information-block">
        <h3>
          <DestinationIcon order={order} />
          Destination {order} - Load information
        </h3>
        <div className="information-table">
          <Table
            variant="dark"
            rowKey="uuid"
            className="information-table"
            columns={
              columnsConfig(
                isEditMode,
                goodCategoriesOptions,
                loadingUnitsOptions,
                wvUnitsOptions,
                stackableOptions,
                deleteItem,
              ) as any
            }
            data={mapTableItemsToComponent(lineItems as any)}
          />
          {isEditMode && lineItems.length < MAX_LINE_ITEMS_COUNT ? (
            <Button
              text="Add line item"
              icon={<FileNewIcon />}
              className="add-button"
              onClick={() =>
                setPoints((prevPoints) => {
                  const newArray = [...prevPoints];
                  const idx = newArray.findIndex(
                    ({ destination: dest }) => dest.uuid === destination.uuid,
                  );
                  if (idx >= 0) {
                    newArray[idx].lineItems.push({
                      isDraft: true,
                      uuid: uuidv4(),
                      destination: destination.uuid as any,
                      recipient: recipient.uuid as any,
                    });
                  }
                  return newArray;
                })
              }
            />
          ) : null}
        </div>
        <Modal
          footer={null}
          title={
            <div className="modal-title">
              <p>Delete line item</p>
            </div>
          }
          width="40%"
          centered
          visible={Boolean(deletingItemApprove)}
          onCancel={() => setDeletingItemApprove(null)}
        >
          <p className="deleting-modal-info">
            Are you sure you want to delete this line item?
            <br />
            It will not be possible to restore the data
          </p>
          <div className="button-container">
            <Button onClick={() => setDeletingItemApprove(null)} variant="danger" text="CANCEL" />
            <Button
              onClick={() => {
                if (deletingItemApprove) {
                  setPoints((prevPoints) => {
                    const newArray = [...prevPoints];
                    const idx = newArray.findIndex(
                      ({ destination: dest }) => dest.uuid === destination.uuid,
                    );
                    if (idx >= 0) {
                      newArray[idx].lineItems = newArray[idx].lineItems.filter(
                        (item) => item.uuid !== deletingItemApprove,
                      );
                    }
                    return newArray;
                  });
                  setDeletingItems((prev) => [...prev, deletingItemApprove]);
                  setDeletingItemApprove(null);
                }
              }}
              variant="submit"
              text="CONFIRM"
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default PointBlock;
